import React, { createContext, useContext, useState, useEffect } from 'react';

const SidebarContext = createContext();

export const SidebarProvider = ({ children }) => {
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    // Check if we're on mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
    
    if (isMobile) {
      return false; // Always collapsed on mobile
    }
    
    // Get initial state from localStorage for desktop, default to true
    const saved = localStorage.getItem('sidebarExpanded');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const toggleSidebar = () => {
    setSidebarExpanded(prev => {
      const newState = !prev;
      // Only save to localStorage on desktop
      if (window.innerWidth >= 1024) {
        localStorage.setItem('sidebarExpanded', JSON.stringify(newState));
      }
      return newState;
    });
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        // Close sidebar on mobile
        setSidebarExpanded(false);
      } else {
        // On desktop, restore from localStorage
        const saved = localStorage.getItem('sidebarExpanded');
        setSidebarExpanded(saved !== null ? JSON.parse(saved) : true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Only sync with localStorage on desktop
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      localStorage.setItem('sidebarExpanded', JSON.stringify(sidebarExpanded));
    }
  }, [sidebarExpanded]);

  const value = {
    sidebarExpanded,
    setSidebarExpanded,
    toggleSidebar,
    handleSidebarToggle: toggleSidebar // Alias for compatibility
  };

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}; 